import { useEffect, useMemo, useRef } from "react";
import { skipToken } from "@reduxjs/toolkit/query";

import { PermissionRoles } from "@/enum";

import { FeaturesCodes } from "@/pages/Private/pages/Users/enum/codes";
import { useHasPermissions } from "@/utils/useHasPermissions";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";

import { useGetDeepResearchPendingQuery } from "./deepResearch.api";

import type { DeepResearchResultController } from "@/pages/Private/pages/ProfileTracker/providers/useDeepResearchResultController";

/**
 * How often a page asks again while one of its leads has a research queued or running. The
 * socket ends a run; the poll is what moves it from queued to running (no event says so) and what
 * ends one whose socket event was lost.
 */
export const DEEP_RESEARCH_POLL_MS = 30_000;

/** The ids to ask about, sorted, so the same leads in another order ask the same question. */
export const runQueryIds = (prospectIds: number[]): number[] =>
	[...new Set(prospectIds.filter((id) => Number.isInteger(id) && id > 0))].sort((a, b) => a - b);

type RunSeeder = Pick<DeepResearchResultController, "seedRuns" | "isPending">;

/**
 * Asks the server where these prospects' deep research stands and hands the answer to the
 * controller (PREC-4052): queued and running runs with their start, failures with their reason,
 * and the end of every run the server no longer reports. Asked again on every change of leads,
 * and every {@link DEEP_RESEARCH_POLL_MS} while one of them is in flight.
 *
 * This replaces the page's own pending stamp, which a reload in the extension lost and which
 * expired after ten minutes while a research may wait thirty for a slot (PREC-4218).
 *
 * Asked only by a user the endpoint admits (the DEEP_RESEARCH permission, in a company with the
 * feature on): the provider runs this on every list page, and a 403 on a query sends the whole
 * app to the error page (`rtkRejectQueryMiddleware`).
 */
export function useSeedDeepResearchRuns(prospectIds: number[], { seedRuns, isPending }: RunSeeder) {
	const key = runQueryIds(prospectIds).join(",");
	const ids = useMemo(() => (key ? key.split(",").map(Number) : []), [key]);
	const anyInFlight = ids.some((id) => isPending(id));
	const { hasPermissions } = useHasPermissions();
	const isFeatureActive = useIsFeatureActive(FeaturesCodes.DEEP_RESEARCH);
	const mayAsk = isFeatureActive === true && hasPermissions([PermissionRoles.DEEP_RESEARCH]);

	const query = mayAsk && ids.length ? ids : skipToken;
	const { data, startedTimeStamp } = useGetDeepResearchPendingQuery(query, {
		refetchOnMountOrArgChange: true,
		pollingInterval: anyInFlight ? DEEP_RESEARCH_POLL_MS : 0,
	});
	const seededAnswerRef = useRef<typeof data>(undefined);

	useEffect(() => {
		if (!data || seededAnswerRef.current === data) {
			return;
		}

		seededAnswerRef.current = data;
		seedRuns(data.runs ?? [], ids, startedTimeStamp ?? Date.now());
	}, [data, ids, startedTimeStamp, seedRuns]);
}
